<template>
  <div class="info-card">
    <div class="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
      <div class="d-flex align-items-center gap-2">
        <div class="card-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
        </div>
        <h3 class="mb-0">Gondviselő adatai</h3>
      </div>
      <div class="d-flex align-items-center gap-2">
        <span v-if="hasParent" class="badge bg-success-subtle text-success border border-success-subtle rounded-pill px-2.5 py-1 small">
          ✓ Összekapcsolva
        </span>
        <span v-else class="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle rounded-pill px-2.5 py-1 small">
          ⚠️ Nincs összekapcsolva
        </span>
        <button
          v-if="hasParent && (!isParent || isDancerRoute)"
          class="modern-btn modern-btn--primary btn-sm px-3 py-1 text-nowrap d-inline-flex align-items-center gap-1.5"
          @click="$emit('open-message-parent-modal')"
        >
          ✉️ Üzenet a szülőnek
        </button>
      </div>
    </div>
    
    <div v-if="hasParent" class="info-list">
      <!-- Email -->
      <div class="info-item">
        <div class="info-label">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
            <polyline points="22,6 12,13 2,6"/>
          </svg>
          Email
        </div>
        <div class="info-value" v-if="(isAdmin || canEdit) && isEditingEnabled" @dblclick="editField('parent2email')">
          <span v-if="!isEditingField.parent2email">{{ parentEmail || 'Nincs megadva (kattints kétszer a megadáshoz)' }}</span>
          <div v-if="isEditingField.parent2email" class="d-flex gap-2 w-100 align-items-center mt-1">
            <input v-model="inviteEmailInput" @keydown.enter="submitInvitation" class="modern-input flex-grow-1" placeholder="gondviselo@email.com">
            <button class="modern-btn modern-btn--primary btn-sm text-nowrap rounded-3 px-3 py-1.5" @click="submitInvitation">
              Mentés & Meghívó
            </button>
          </div>
        </div>
        <div class="info-value" v-else>{{ parentEmail || 'Nincs megadva' }}</div>
      </div>

      <!-- Vezetéknév -->
      <div class="info-item">
        <div class="info-label">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
          Vezetéknév
        </div>
        <div class="info-value" v-if="(isAdmin || canEdit) && isEditingEnabled" @dblclick="editField('parent2xfamily_name')">
          <span v-if="!isEditingField.parent2xfamily_name">{{ parentFamilyName || 'Nincs megadva' }}</span>
          <input
            v-if="isEditingField.parent2xfamily_name && childInfo.parent2xfamily_name"
            v-model="childInfo.parent2xfamily_name.String"
            @keydown.enter="saveParentField('parent2xfamily_name', childInfo.parent2xfamily_name.String)"
            @blur="saveParentField('parent2xfamily_name', childInfo.parent2xfamily_name.String)"
            class="modern-input"
          >
        </div>
        <div class="info-value" v-else>{{ parentFamilyName || 'Nincs megadva' }}</div>
      </div>

      <!-- Keresztnév -->
      <div class="info-item">
        <div class="info-label">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
          Keresztnév
        </div>
        <div class="info-value" v-if="(isAdmin || canEdit) && isEditingEnabled" @dblclick="editField('parent2xsure_name')">
          <span v-if="!isEditingField.parent2xsure_name">{{ parentSureName || 'Nincs megadva' }}</span>
          <input
            v-if="isEditingField.parent2xsure_name && childInfo.parent2xsure_name"
            v-model="childInfo.parent2xsure_name.String"
            @keydown.enter="saveParentField('parent2xsure_name', childInfo.parent2xsure_name.String)"
            @blur="saveParentField('parent2xsure_name', childInfo.parent2xsure_name.String)"
            class="modern-input"
          >
        </div>
        <div class="info-value" v-else>{{ parentSureName || 'Nincs megadva' }}</div>
      </div>

      <!-- Telefonszám -->
      <div class="info-item">
        <div class="info-label">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
          </svg>
          Telefonszám
        </div>
        <div class="info-value" v-if="(isAdmin || canEdit) && isEditingEnabled" @dblclick="editField('parent2xphone')">
          <span v-if="!isEditingField.parent2xphone">{{ parentPhone || 'Nincs megadva' }}</span>
          <input
            v-if="isEditingField.parent2xphone && childInfo.parent2xphone"
            v-model="childInfo.parent2xphone.String"
            @keydown.enter="saveParentField('parent2xphone', childInfo.parent2xphone.String)"
            @blur="saveParentField('parent2xphone', childInfo.parent2xphone.String)"
            class="modern-input"
          >
        </div>
        <div class="info-value" v-else>{{ parentPhone || 'Nincs megadva' }}</div>
      </div>

      <!-- Lakcím -->
      <div class="info-item">
        <div class="info-label">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
          Lakcím
        </div>
        <div class="info-value" v-if="(isAdmin || canEdit) && isEditingEnabled" @dblclick="editField('parent2xaddr')">
          <span v-if="!isEditingField.parent2xaddr">{{ parentAddr || 'Nincs megadva' }}</span>
          <input
            v-if="isEditingField.parent2xaddr && childInfo.parent2xaddr"
            v-model="childInfo.parent2xaddr.String"
            @keydown.enter="saveParentField('parent2xaddr', childInfo.parent2xaddr.String)"
            @blur="saveParentField('parent2xaddr', childInfo.parent2xaddr.String)"
            class="modern-input"
          >
        </div>
        <div class="info-value" v-else>{{ parentAddr || 'Nincs megadva' }}</div>
      </div>

      <!-- Dancer direct message tip -->
      <div v-if="!isParent || isDancerRoute" class="mt-3 p-3 rounded-3 parent-message-tip-box d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div class="d-flex align-items-center gap-2">
          <span class="fs-5">💬</span>
          <div class="small">
            <div class="fw-semibold parent-tip-title">Kérdésed vagy kérésed van a szülődhöz?</div>
            <div class="text-muted">Közvetlenül küldhetsz üzenetet a megadott e-mail címre ({{ parentEmail }}).</div>
          </div>
        </div>
        <button class="modern-btn modern-btn--primary btn-sm px-3 py-1.5" @click="$emit('open-message-parent-modal')">
          ✉️ Üzenet küldése
        </button>
      </div>
    </div>

    <!-- Unlinked State Banner & Form -->
    <div v-else class="p-3">
      <div class="alert unlinked-parent-alert d-flex align-items-start gap-3 p-3 rounded-3 mb-3 shadow-xs">
        <span class="fs-4">⚠️</span>
        <div>
          <div class="fw-bold">Nincs gondviselő hozzárendelve</div>
          <div class="small opacity-90">Ehhez a táncoshoz jelenleg még nem kapcsolódik regisztrált szülői fiók. Kérjük, küldj meghívót a gondviselőnek, hogy elérhesse a táncos óráit, adatait és befizetéseit!</div>
        </div>
      </div>

      <!-- Gondviselő meghívása e-mailben -->
      <div class="invite-box-container p-3.5 rounded-3 border">
        <div class="d-flex align-items-center mb-2">
          <div class="invite-badge me-2.5">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
          </div>
          <div class="invite-box-title fw-bold">Gondviselő meghívása e-mailben</div>
        </div>
        <p class="invite-box-desc mb-3 text-muted small">
          Add meg a gondviselő e-mail címét a regisztrációs meghívó kiküldéséhez!
        </p>
        <div class="d-flex gap-2 align-items-center flex-wrap flex-sm-nowrap">
          <input
            type="email"
            v-model="inviteEmailInput"
            placeholder="gondviselo@email.com"
            class="modern-input flex-grow-1"
            :disabled="isInvitingParent"
            @keydown.enter="submitInvitation"
          >
          <button
            class="modern-btn modern-btn--primary px-4 py-2 text-nowrap"
            @click="submitInvitation"
            :disabled="isInvitingParent || !inviteEmailInput"
          >
            <span v-if="isInvitingParent">Küldés...</span>
            <span v-else>✉️ Meghívó küldése</span>
          </button>
        </div>
        <div v-if="inviteMessage" class="mt-2.5 small fw-semibold" :class="inviteSuccess ? 'text-success' : 'text-danger'">
          {{ inviteMessage }}
        </div>
      </div>
    </div>

    <!-- Értesítési címzettek tájékoztató sáv -->
    <div class="p-3 pt-0">
      <div class="notification-routing-note p-3 rounded-3 small" style="background: rgba(99, 102, 241, 0.08); border: 1px solid rgba(99, 102, 241, 0.2); color: #3730a3;">
        <div class="d-flex align-items-center gap-2 mb-1 fw-bold">
          <span>📧</span> Értesítések és fizetési visszaigazolások kézbesítése:
        </div>
        <div v-if="childInfo && childInfo.dancer_email && hasParent">
          Mivel a táncosnak és a szülőnek is van megadott e-mail címe, a befizetési visszaigazolások és fontos értesítések <strong>mindkét félnek</strong> (táncosnak és szülőnek is) kiküldésre kerülnek.
        </div>
        <div v-else-if="hasParent">
          A táncosnak nincs külön e-mail címe, ezért a befizetési visszaigazolások és értesítések a <strong>gondviselő e-mail címére</strong> érkeznek.
        </div>
        <div v-else-if="childInfo && childInfo.dancer_email">
          Gondviselő nincs hozzárendelve, ezért az értesítések kizárólag a <strong>táncos e-mail címére</strong> érkeznek.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ChildGuardianCard',
  props: {
    childInfo: {
      type: Object,
      default: null
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    isEditingEnabled: {
      type: Boolean,
      default: false
    },
    isParent: {
      type: Boolean,
      default: false
    },
    isDancerRoute: {
      type: Boolean,
      default: false
    },
    isInvitingParent: {
      type: Boolean,
      default: false
    },
    inviteMessage: {
      type: String,
      default: ''
    },
    inviteSuccess: {
      type: Boolean,
      default: false
    }
  },
  emits: ['save-parent-field', 'send-parent-invitation', 'open-message-parent-modal'],
  data() {
    return {
      inviteEmailInput: '',
      isEditingField: {
        parent2email: false,
        parent2xfamily_name: false,
        parent2xsure_name: false,
        parent2xphone: false,
        parent2xaddr: false
      }
    };
  },
  computed: {
    hasParent() {
      if (!this.childInfo) return false;
      if (typeof this.childInfo.has_parent === 'boolean') {
        return this.childInfo.has_parent;
      }
      return !!this.parentEmail;
    },
    parentEmail() {
      if (!this.childInfo || !this.childInfo.parent2Email) return '';
      if (typeof this.childInfo.parent2Email === 'object') {
        return this.childInfo.parent2Email.String || '';
      }
      return this.childInfo.parent2Email || '';
    },
    parentFamilyName() {
      if (!this.childInfo || !this.childInfo.parent2xfamily_name) return '';
      if (typeof this.childInfo.parent2xfamily_name === 'object') {
        return this.childInfo.parent2xfamily_name.String || '';
      }
      return this.childInfo.parent2xfamily_name || '';
    },
    parentSureName() {
      if (!this.childInfo || !this.childInfo.parent2xsure_name) return '';
      if (typeof this.childInfo.parent2xsure_name === 'object') {
        return this.childInfo.parent2xsure_name.String || '';
      }
      return this.childInfo.parent2xsure_name || '';
    },
    parentPhone() {
      if (!this.childInfo || !this.childInfo.parent2xphone) return '';
      if (typeof this.childInfo.parent2xphone === 'object') {
        return this.childInfo.parent2xphone.String || '';
      }
      return this.childInfo.parent2xphone || '';
    },
    parentAddr() {
      if (!this.childInfo || !this.childInfo.parent2xaddr) return '';
      if (typeof this.childInfo.parent2xaddr === 'object') {
        return this.childInfo.parent2xaddr.String || '';
      }
      return this.childInfo.parent2xaddr || '';
    }
  },
  methods: {
    editField(field) {
      this.isEditingField[field] = true;
    },
    saveParentField(field, value) {
      this.isEditingField[field] = false;
      this.$emit('save-parent-field', { field, email: this.parentEmail, value });
    },
    submitInvitation() {
      if (!this.inviteEmailInput) return;
      this.$emit('send-parent-invitation', this.inviteEmailInput);
      this.isEditingField.parent2email = false;
    }
  }
};
</script>

<style scoped>
.info-card {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  overflow: hidden;
  border: 1px solid var(--color-border);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem 2rem;
  background: var(--color-background-soft);
  border-bottom: 1px solid var(--color-border);
}

.card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: var(--button-primary, #2563eb);
  border-radius: 12px;
  color: white;
}

.card-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text);
}

.info-list {
  padding: 1.5rem 2rem;
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid var(--color-border);
}

.info-item:last-child {
  border-bottom: none;
}

.info-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 140px;
  font-weight: 600;
  color: var(--color-text);
}

.info-label svg {
  color: var(--button-primary, #2563eb);
  flex-shrink: 0;
}

.info-value {
  flex: 1;
  color: var(--color-text-muted);
  word-break: break-word;
}

.parent-message-tip-box {
  background-color: var(--color-background-soft);
  border: 1px solid var(--color-border);
}

.unlinked-parent-alert {
  background-color: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
}

.invite-box-container {
  background-color: var(--color-background-soft);
  border-color: var(--color-border) !important;
}

.invite-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(37, 99, 235, 0.1);
  color: var(--primary-blue, #2563eb);
}

[data-theme="dark"] .parent-message-tip-box {
  background-color: rgba(255, 255, 255, 0.04) !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
}

[data-theme="dark"] .unlinked-parent-alert {
  background-color: rgba(245, 158, 11, 0.18) !important;
  color: #fde68a !important;
  border-color: rgba(245, 158, 11, 0.35) !important;
}

[data-theme="dark"] .notification-routing-note {
  background: rgba(99, 102, 241, 0.15) !important;
  border-color: rgba(99, 102, 241, 0.35) !important;
  color: #c7d2fe !important;
}
</style>
